import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { ArtReproduction, Review } from '../types';
import { getArtworkDisplayUrl, useViewportWidth } from '../hooks/useArtworkDisplayUrl';
import { API_BASE } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import ArtworkModal from '../components/gallery/ArtworkModal';
import SaleBadge from '../components/SaleBadge';
import ArtistTierBadge from '../components/ArtistTierBadge';
import './ArtworkDetail.css';

const ArtworkDetail: React.FC = () => {
  const { artworkId } = useParams<{ artworkId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [searchParams] = useSearchParams();
  
  const [artwork, setArtwork] = useState<ArtReproduction | null>(null);
  const [relatedArtworks, setRelatedArtworks] = useState<ArtReproduction[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedSizePrice, setSelectedSizePrice] = useState<{ size: string; price: number; isOriginal?: boolean } | undefined>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!artworkId) {
        navigate('/art');
        return;
      }

      setLoading(true);
      try {
        const artworkRes = await api.get<ArtReproduction>(`/art/${artworkId}`).catch(() => ({ data: null }));
        
        setArtwork(artworkRes.data);

        const reviewsRes = await api.get<Review[]>(`/reviews/artwork/${artworkId}`).catch(() => ({ data: [] }));
        setReviews(reviewsRes.data || []);
        
        // Fetch related artworks from the same artist
        if (artworkRes.data?.artistId) {
          const relatedRes = await api.get<ArtReproduction[]>('/art', { 
            params: { 
              artistId: artworkRes.data.artistId
            } 
          }).catch(() => ({ data: [] }));
          
          // Filter out current artwork from related
          const related = (relatedRes.data || []).filter(a => a.id !== parseInt(artworkId));
          setRelatedArtworks(related.slice(0, 3));
        }
        
        // Set initial size
        if (artworkRes.data?.sizePrices?.[0]) {
          const firstSize = artworkRes.data.sizePrices[0];
          setSelectedSizePrice({
            size: firstSize.size,
            price: firstSize.price,
            isOriginal: firstSize.isOriginal
          });
        }
      } catch (error) {
        console.error('Failed to fetch artwork detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artworkId, navigate, searchParams]);

  const handleSizeSelect = (artworkId: number, sizePrice: { size: string; price: number; isOriginal?: boolean }): void => {
    setSelectedSizePrice(sizePrice);
  };

  const handleAddToCart = async (artwork: ArtReproduction, options?: { material?: string; frame?: string; hanging?: string; price?: number }): Promise<void> => {
    setIsSubmitting(true);
    try {
      let productId: number;
      const productsRes = await api.get<any[]>('/products', { params: { artReproductionId: artwork.id } });
      const products = productsRes.data ?? [];
      const existing = products.length === 1
        ? products[0]
        : products.find((p: any) => p.size === selectedSizePrice?.size) ?? products[0];

      if (existing) {
        productId = existing.id;
      } else {
        const size = selectedSizePrice?.size ?? 'Standard';
        const price = selectedSizePrice?.price ?? artwork.price ?? 0;
        const isOriginal = selectedSizePrice?.isOriginal ?? false;
        const productData = {
          name: `${artwork.title}${size !== 'Standard' ? ` - ${size}` : ''}`,
          description: artwork.description || `Art by ${artwork.artistName || 'Unknown Artist'}`,
          price,
          imageUrl: artwork.imageUrl,
          size,
          isCustomPrint: false,
          artReproductionId: artwork.id,
          isOriginal,
        };
        const createRes = await api.post<{ id: number }>('/products', productData);
        productId = createRes.data.id;
      }

      await api.post('/cart', {
        productId,
        quantity: 1
      });

      setIsModalOpen(false);
      success('Added to cart!');
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to add to cart');
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewportWidth = useViewportWidth();
  const buildImageSrc = (imageUrl?: string): string | null => {
    if (!imageUrl) return null;
    return imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`;
  };

  if (loading) {
    return (
      <div className="artwork-detail-page container">
        <div className="loading">Loading artwork...</div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="artwork-detail-page container">
        <div className="error">Artwork not found</div>
      </div>
    );
  }

  const fullImageUrl = buildImageSrc(artwork.imageUrl);

  return (
    <div className="artwork-detail-page">
      <div className="container">
        <button className="back-button" onClick={() => navigate('/art')}>
          <i className="fas fa-arrow-left"></i> Back to Shop
        </button>

        <div className="artwork-detail">
          <div className="artwork-detail__image">
            {fullImageUrl && (
              <img
                src={fullImageUrl}
                alt={artwork.title}
                draggable={false}
                onContextMenu={(event) => event.preventDefault()}
                onDragStart={(event) => event.preventDefault()}
              />
            )}
            {artwork.activeSale && (
              <div className="artwork-detail__badge">
                <SaleBadge sale={artwork.activeSale} className="sale-badge--high-visibility" />
              </div>
            )}
          </div>

          <div className="artwork-detail__info">
            <div className="artwork-detail__header">
              <h1>
                {artwork.title}
                {artwork.activeSale && <SaleBadge sale={artwork.activeSale} className="sale-badge--inline" />}
              </h1>
              {artwork.artistName && artwork.artistId && (
                <p className="artist-link">
                  by <a href={`/artists/${artwork.artistId}/shop`} onClick={(e) => { e.preventDefault(); navigate(`/artists/${artwork.artistId}/shop`); }}>
                    {artwork.artistName}
                  </a>
                  <ArtistTierBadge tier={artwork.artistTier} />
                </p>
              )}
            </div>

            {artwork.description && (
              <div className="artwork-detail__description">
                <p>{artwork.description}</p>
              </div>
            )}

            {artwork.tags && artwork.tags.length > 0 && (
              <div className="artwork-detail__tags">
                {artwork.tags.map((tag) => (
                  <span key={tag.id} className="tag-badge">
                    {tag.name}
                  </span>
                ))}
              </div>
            )}

            {artwork.sizePrices && artwork.sizePrices.length > 0 && (
              <div className="artwork-detail__sizes">
                <h3>Available Sizes</h3>
                <div className="sizes-list">
                  {artwork.sizePrices.map((sp, index) => (
                    <button
                      key={index}
                      className={`size-option ${selectedSizePrice?.size === sp.size ? 'selected' : ''}`}
                      onClick={() => setSelectedSizePrice({ size: sp.size, price: sp.price, isOriginal: sp.isOriginal })}
                    >
                      <span className="size-name">{sp.size}</span>
                      {sp.width && sp.height && (
                        <span className="size-dimensions">{sp.width}" × {sp.height}"</span>
                      )}
                      <span className="size-price">
                        ${(artwork.salePrice && artwork.salePrice < sp.price) ? artwork.salePrice.toFixed(2) : sp.price.toFixed(2)}
                        {artwork.salePrice && artwork.salePrice < sp.price && artwork.originalPrice && (
                          <span className="original-price">${artwork.originalPrice.toFixed(2)}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="artwork-detail__actions">
              <button
                className="btn btn-primary btn-large"
                onClick={() => setIsModalOpen(true)}
                style={{ width: '100%' }}
              >
                View Options & Add to Cart
              </button>
            </div>
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="artwork-reviews">
            <h2>
              Reviews{' '}
              <span className="artwork-reviews__average">
                <i className="fas fa-star"></i>{' '}
                {(reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)}
                {' '}({reviews.length})
              </span>
            </h2>
            <div className="artwork-reviews__list">
              {reviews.map((review) => (
                <div key={review.id} className="artwork-review">
                  <div className="artwork-review__header">
                    <span className="artwork-review__stars" aria-label={`${review.rating} out of 5 stars`}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={star <= review.rating ? 'fas fa-star' : 'far fa-star'}
                        ></i>
                      ))}
                    </span>
                    <span className="artwork-review__author">{review.userName || 'Customer'}</span>
                    <span className="artwork-review__date">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {review.comment && <p className="artwork-review__comment">{review.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {relatedArtworks.length > 0 && (
          <div className="related-artworks">
            <h2>More from {artwork.artistName}</h2>
            <div className="related-artworks-grid">
              {relatedArtworks.map((related) => (
                <div
                  key={related.id}
                  className="related-artwork-card"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${related.title}`}
                  onClick={() => navigate(`/artworks/${related.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/artworks/${related.id}`);
                    }
                  }}
                >
                  <img
                    src={buildImageSrc(getArtworkDisplayUrl(related, viewportWidth)) || '/placeholder-image.svg'}
                    alt={related.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const fallback = buildImageSrc(related.thumbnailUrl || related.imageUrl);
                      if (fallback && target.src !== fallback) target.src = fallback;
                    }}
                  />
                  <p>{related.title}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <ArtworkModal
          isOpen={isModalOpen}
          artwork={artwork}
          selectedSize={selectedSizePrice}
          onSizeSelect={handleSizeSelect}
          onAddToCart={handleAddToCart}
          onClose={() => setIsModalOpen(false)}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ArtworkDetail;
